import { History } from 'lucide-react';
import { useRecordHistory } from '../hooks/useApi';
import type { AuditLog } from '../types';
import { ACTION_LABELS, formatDateTime } from '../utils/helpers';
import { Badge, type BadgeVariant } from './ui/Badge';
import { EmptyState } from './ui/Card';
import { PageLoader } from './ui/Spinner';
import { t, tr } from '../i18n';

export const ACTION_VARIANT: Record<string, BadgeVariant> = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'danger',
  LOGIN: 'info',
  IMPORT: 'primary',
};

/** Human-friendly names of audited fields. */
export const FIELD_LABELS: Record<string, string> = tr({
  license_plate: 'ŠPZ',
  brand: 'Značka',
  model: 'Model',
  year: 'Rok výroby',
  vin: 'VIN',
  fuel_type: 'Palivo',
  status: 'Stav',
  current_km: 'Stav km',
  notes: 'Poznámky',
  created_by: 'Vytvoril (ID)',
  first_name: 'Meno',
  last_name: 'Priezvisko',
  employee_id: 'Osobné číslo',
  department: 'Oddelenie',
  phone: 'Telefón',
  email: 'E-mail',
  license_number: 'Číslo VP',
  license_expiry: 'Platnosť VP',
  is_active: 'Aktívne',
  car_id: 'Vozidlo (ID)',
  driver_id: 'Vodič (ID)',
  assigned_at: 'Pridelené',
  returned_at: 'Vrátené',
  start_km: 'Počiatočné km',
  end_km: 'Konečné km',
  purpose: 'Účel',
  service_date: 'Dátum servisu',
  service_type: 'Typ servisu',
  description: 'Popis',
  km_at_service: 'Km pri servise',
  cost: 'Cena',
  workshop: 'Servis',
  invoice_number: 'Faktúra',
  next_service_date: 'Ďalší servis',
  next_service_km: 'Ďalší servis (km)',
  title: 'Názov',
  doc_type: 'Typ dokumentu',
  file_path: 'Súbor',
  original_filename: 'Názov súboru',
  mime_type: 'Typ súboru',
  file_size: 'Veľkosť',
  uploaded_by: 'Nahral (ID)',
  uploaded_at: 'Nahrané',
  expiry_date: 'Platnosť do',
  username: 'Používateľ',
  full_name: 'Celé meno',
  role: 'Rola',
  last_login: 'Posledné prihlásenie',
  company_name: 'Názov spoločnosti',
  primary_color: 'Hlavná farba',
  secondary_color: 'Doplnková farba',
  logo_light_url: 'Logo',
  logo_dark_url: 'Logo (tmavé)',
});

export function fieldLabel(field: string | null) {
  return field ? FIELD_LABELS[field] ?? field : '';
}

export function ValueChange({ log }: { log: AuditLog }) {
  if (!log.field_name) return <span className="text-secondary-600">{log.new_value ?? ''}</span>;
  return (
    <span className="break-words">
      <span className="font-medium text-secondary-700">{fieldLabel(log.field_name)}: </span>
      {log.action !== 'CREATE' && <span className="text-red-700 line-through decoration-red-300">{log.old_value ?? '∅'}</span>}
      {log.action === 'UPDATE' && <span className="mx-1 text-secondary-400">→</span>}
      {log.action !== 'DELETE' && <span className="text-green-700">{log.new_value ?? '∅'}</span>}
    </span>
  );
}

/** Groups field-level audit rows made by one request into one timeline entry. */
function groupLogs(logs: AuditLog[]) {
  const groups: { key: string; logs: AuditLog[] }[] = [];
  for (const log of logs) {
    const key = `${log.timestamp.slice(0, 19)}|${log.action}|${log.user_id}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.logs.push(log);
    else groups.push({ key, logs: [log] });
  }
  return groups;
}

export function RecordHistory({ table, recordId }: { table: string; recordId: number }) {
  const { data, isLoading } = useRecordHistory(table, recordId);
  if (isLoading) return <PageLoader />;
  if (!data?.length) return <EmptyState icon={<History className="h-10 w-10" />} title={t('Žiadne zmeny')} />;

  return (
    <ol className="relative space-y-4 border-l border-secondary-200 pl-6">
      {groupLogs(data).map((group) => {
        const first = group.logs[0];
        return (
          <li key={`${group.key}-${first.id}`} className="relative">
            <span className="absolute -left-[31px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-primary-500 ring-1 ring-primary-200" />
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant={ACTION_VARIANT[first.action] ?? 'default'}>{ACTION_LABELS[first.action] ?? first.action}</Badge>
              <span className="font-medium text-secondary-800">{first.user?.full_name || first.user?.username || t('Systém')}</span>
              <span className="text-secondary-500">{formatDateTime(first.timestamp)}</span>
              {first.ip_address && <span className="font-mono text-xs text-secondary-400">{first.ip_address}</span>}
            </div>
            <ul className="mt-1.5 space-y-0.5 text-sm">
              {group.logs.map((log) => (
                <li key={log.id}>
                  <ValueChange log={log} />
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ol>
  );
}
